import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  Checkbox,
  useToast,
  Table,
  Tbody,
  Tr,
  Td,
  Th,
  Thead,
  Flex,
  FormControl,
  Select,
  Box,
  Button,
  HStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import "./usergroups.css";
import { useMsal } from "@azure/msal-react";
import { useAppContext } from "../../../context/AppContext";
import {
  fetchUserGroupsMapping,
  updateUserGroupMapping,
} from "../../../services/AdminServices/UserGroupMapping";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: number | null;
  user: any[]; // You might want to refine this type to match the structure of quickLinks
  onSave: (updatedUsers: any[]) => void; // Refine 'any' if possible
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  groupId,
  user,
  onSave,
}) => {
  const [users, setUsers] = useState<any[]>([]);
  const [allChecked, setAllChecked] = useState<boolean>(false); // Header checkbox state
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  // api integration
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast(); // To show toasts

  // tenant id
  const { accessToken, userData } = useAppContext();
  const tenantID= userData?.tenantID || 0;

  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const [itemsPerPage, setItemsPerPage] = useState(5); // Items per page

  // Toggle the selection of a user
  const handleToggleUser = (userID: number) => {
    setSelectedUsers(
      (prev) =>
        prev.includes(userID)
          ? prev.filter((id) => id !== userID) // Uncheck
          : [...prev, userID] // Check
    );
  };

  // Toggle all users across all pages
  const handleToggleAll = () => {
    const allUserIDs = filteredUsers.map((user) => user.userID); // Use userID directly
    if (selectedUsers.length === users.length) {
      // Uncheck all users
      setSelectedUsers([]);
    } else {
      // Check all filtered users
      setSelectedUsers(allUserIDs);
    }
  };

  // update the mapping of user in usergroup
  const handleSave = async () => {
    if (!accessToken) {
      toast({
        position: "top",
        title: "Authentication Error",
        description: "Access token is missing. Please log in again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      // Prepare the selected user details
      const selectedUserDetails = users.filter((user) =>
        selectedUsers.includes(user.userID)
      );
      console.log("Selected users are:", selectedUserDetails);

      // Extract only the userID values
      const userIds = selectedUserDetails.map((user) => user.userID);

      // Call the service to update quick links group mapping
      await updateUserGroupMapping(
        accessToken,
        groupId ||0,
        userIds ,// Pass only the IDs
        tenantID
      );

      console.log("All user group mappings added successfully");
      onSave(selectedUserDetails); // Update local state
      onClose(); // Close modal
    } catch (error) {
      console.error("Error occurred during API call:", error);
      setError("An error occurred while saving users.");
    }
  };

  const getUsersGroupsMapping = async () => {
    try {
      if (accessToken) {
        const data: {
          userID: number;
          userName: string;
          office365MailId: string;
          isChecked: boolean;
        }[] = await fetchUserGroupsMapping(accessToken, groupId || 0, tenantID);
        console.log("Get User Group Mapping Data GET ===>", data);

        // Sort users so that checked ones come first
        const sortedUsers = data.sort(
          (a: { isChecked: boolean }, b: { isChecked: boolean }) =>
            Number(b.isChecked) - Number(a.isChecked)
        );

        setUsers(sortedUsers);

        // Initialize selected users based on isChecked
        const initiallySelected = data
          .filter((user) => user.isChecked)
          .map((user) => user.userID);

        setSelectedUsers(initiallySelected);
      } else {
        setError("Access token is missing.");
      }
    } catch (error) {
      setError("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getUsersGroupsMapping();
    }
  }, [accessToken, groupId, isOpen]); // Fetch data when accessToken, groupId, or modal visibility changes

  useEffect(() => {
    // Reset to first page when search term changes
    setCurrentPage(1);
  }, [searchQuery]);


  // Close the modal and reset state
  const handleClose = () => {
    setSearchQuery(""); // Reset search query
    setCurrentPage(1); // Reset pagination
    setAllChecked(false); // Reset the "Check All" state
    onClose(); // Close the modal
  };

  // Change items per page
  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to the first page when items per page changes
  };

  // Change page
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value);

  // Filter users based on search query
  const filteredUsers = users.filter(
    (user) =>
      (user.userName?.toLowerCase() || "").includes(
        searchQuery.toLowerCase()
      ) || (user.office365MailId?.toLowerCase() || "").includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const totalUsers = filteredUsers.length;
  const totalPages = Math.ceil(totalUsers / itemsPerPage); // Calculate total pages
  const indexOfLastUser = currentPage * itemsPerPage;
  const indexOfFirstUser = indexOfLastUser - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser); // Slice the filtered users

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={"xl"}>
      <ModalOverlay />
      <ModalContent maxWidth="900px" height="650px">
        <ModalHeader>Select Users</ModalHeader>
        <ModalCloseButton />
        <hr style={{ border: "1px solid #black", margin: "10px" }} />
        <Flex justify="space-between" align="center" p={4}>
          {/* Items per page selector */}
          <Select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            maxWidth="200px"
          >
            <option value={5}>5 items per page</option>
            <option value={6}>6 items per page</option>
          </Select>

          {/* Search input */}
          <FormControl maxWidth="300px">
            <Input
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search users..."
            />
          </FormControl>
        </Flex>

        <ModalBody>
          <Table mt={0} className="Table">
            <Thead>
              <Tr>
                <Th width="50px">
                  <Checkbox
                    isChecked={
                      selectedUsers.length === users.length && users.length > 0
                    } // All selected
                    onChange={handleToggleAll}
                    isIndeterminate={
                      selectedUsers.length > 0 &&
                      selectedUsers.length < users.length // Partially selected
                    }
                  />
                </Th>
                <Th>User Name</Th>
                <Th>Email</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentUsers.map((user) => (
                <Tr key={user.userID}>
                  <Td width="50px">
                    <Checkbox
                      isChecked={selectedUsers.includes(user.userID)} // Reflect selection state
                      onChange={() => handleToggleUser(user.userID)}
                    />
                  </Td>
                  <Td>{user.userName}</Td>
                  <Td>{user.office365MailId}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {/* Pagination Info */}
          <Flex justify="space-between" mt={4}>
            <Box>
              Showing {Math.min(totalUsers, indexOfFirstUser + 1)} to{" "}
              {Math.min(indexOfLastUser, totalUsers)} of {totalUsers} entries
            </Box>

            {/* Pagination Controls */}
            <HStack>
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>
              <Select
                value={currentPage}
                onChange={(e) => handlePageChange(Number(e.target.value))}
                maxWidth="70px"
              >
                {Array.from({ length: totalPages }, (_, index) => (
                  <option key={index} value={index + 1}>
                    {index + 1}
                  </option>
                ))}
              </Select>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </HStack>
          </Flex>
          <hr style={{ border: "1px solid #black", margin: "10px" }} />
          {/* Save and Close Buttons */}
          <Flex justify="flex-end" mt={3}>
            <Button onClick={handleSave} colorScheme="blue" mr={3}>
              Save
            </Button>
            <Button onClick={handleClose} variant="outline">
              Close
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UserModal;
